/**
 * SpecialistID — Product PDP modular bundle: VARIANT SWITCH
 * Part of the custom-product-* set (split out of the legacy custom-product.js).
 *
 * Owns variant-selection logic: optionLogic (SKU sync, pricing, qty rules),
 * handleWindowLoad (on-load table price + SKU listeners), and the URL
 * MutationObserver that detects ?variant= param changes and re-runs optionLogic.
 *
 * Public API: SPID.optionLogic(variant_id), SPID.handleWindowLoad()
 * Private: urlObserver, the window load listener.
 *
 * LOAD ORDER: after custom-product-core.js + custom-product-data.js +
 * custom-product-inventory.js + custom-product-pricing.js +
 * custom-product-quantity.js + custom-product-volume-pricing.js,
 * before custom-product.js.
 */
(function () {
  'use strict';

  const SPID = window.SPID;
  const debugLog = SPID.debugLog;
  const loadProductData = SPID.loadProductData;
  const getVariantData = SPID.getVariantData;
  const getVariantPrice = SPID.getVariantPrice;
  const getVariantCompareAtPrice = SPID.getVariantCompareAtPrice;
  const getVariantMetafield = SPID.getVariantMetafield;
  const getProductMetafield = SPID.getProductMetafield;
  const mapMsrpLogic = SPID.mapMsrpLogic;
  const manageStockDisplay = SPID.optional('manageStockDisplay');
  const quantityLogic = SPID.optional('quantityLogic');
  const updateTablePrice = SPID.optional('updateTablePrice');

  function optionLogic(variant_id) {
    const variantIdInput = document.querySelector('input.current_variant_id');
    if (variantIdInput) variantIdInput.value = variant_id;

    // SPID-308: drop the previous variant's quantity rule; manageStockDisplay re-applies
    // the new variant's once its inventory record arrives.
    SPID.state.dropshipRule = null;

    // Get variant data from JSON
    const variant = getVariantData(variant_id);
    if (!variant) {
      console.error('Variant not found:', variant_id);
      return;
    }

    // Use variant data from JSON
    var sku = variant.sku || '';
    var price = getVariantPrice(variant_id);
    var price_num = price;
    var compare_at_price = getVariantCompareAtPrice(variant_id);
    var compare_at_price_num = compare_at_price;

    // Get custom data from metafields only
    var customPriceText = getVariantMetafield(variant_id, 'custom', 'price_text') || '';
    var qty = variant.inventory_quantity || 0;

    // Per-field fallback (per Simon, Jun 2026): resolve minimum and increment
    // INDEPENDENTLY — use the variant-level value when present, otherwise fall back
    // to the product-level value. Mirrors the server-side initial render in
    // snippets/product-quantity-selector.liquid so the stepper stays consistent before
    // and after a variant switch.
    //
    // ATOMIC variant c_f pair (SPID-75, mirrored from the `staging` branch): the variant
    // c_f step is skipped for BOTH fields unless that variant set its own c_f.minimum.
    // An orphan increment — increment set, minimum blank — is a stale hidden value (many
    // date from 2019 and are invisible in admin) and must not override the product rule.
    // Genuine pack config, where the variant carries both fields, is unaffected, and the
    // inventory.* tiers below are untouched.
    var variantCfMinimum = parseInt(getVariantMetafield(variant_id, 'c_f', 'minimum'));
    var variantHasOwnCfRules = !!variantCfMinimum;

    var min = variantCfMinimum
            || parseInt(getVariantMetafield(variant_id, 'inventory', 'minimum'))
            || parseInt(getProductMetafield('c_f', 'minimum'))
            || parseInt(getProductMetafield('inventory', 'minimum'))
            || 1;
    var increment = (variantHasOwnCfRules && parseInt(getVariantMetafield(variant_id, 'c_f', 'increment')))
            || parseInt(getVariantMetafield(variant_id, 'inventory', 'increment'))
            || parseInt(getProductMetafield('c_f', 'increment'))
            || parseInt(getProductMetafield('inventory', 'increment'))
            || 1;

    debugLog(`optionLogic for variant ${variant_id}: min=${min}, increment=${increment}, qty=${qty}, customPriceText=${customPriceText}`);

    const variantSkuEl = document.querySelector('span.variant-sku');
    if (variantSkuEl) variantSkuEl.innerHTML = sku;

    var percent_value = (((compare_at_price_num - price_num) * 100.0) / compare_at_price_num).toFixed(0);

    // Sync qty input to the new variant's min BEFORE mapMsrpLogic runs; otherwise
    // the Total Price gets computed against the previous variant's qty.
    document.querySelectorAll(".quantity__input").forEach(input => {
      input.value = min;
    });

    mapMsrpLogic(variant_id, price);
    manageStockDisplay(variant_id);
    quantityLogic();

    if (customPriceText != "") {
      const priceMinEl = document.querySelector('span.price-min');
      if (priceMinEl) priceMinEl.innerHTML = customPriceText;
      const customPriceEl = document.querySelector(".custom-price-text");
      if (customPriceEl) {
        customPriceEl.innerHTML = customPriceText;
        customPriceEl.classList.remove("hide");
      }
    } else {
      const customPriceEl = document.querySelector(".custom-price-text");
      if (customPriceEl) {
        customPriceEl.classList.add("hide");
        customPriceEl.innerHTML = "";
      }
    }

    if (min != increment) {
      const minQtyEl = document.querySelector(".minimum-quantity-text");
      if (minQtyEl) minQtyEl.classList.remove("hide");
    } else if (min == increment && min > 1) {
      const soldInPack = document.querySelector(".sold-in-pack-text");
      if (soldInPack) soldInPack.classList.remove("hide");
    } else {
      const minQtyEl = document.querySelector(".minimum-quantity-text");
      const soldInPack = document.querySelector(".sold-in-pack-text");
      if (minQtyEl) minQtyEl.classList.add("hide");
      if (soldInPack) soldInPack.classList.add("hide");
    }

    const minQtySpan = document.querySelector(".minimum_qty");
    if (minQtySpan) minQtySpan.innerHTML = min;

    const qtyInputs = document.querySelectorAll(".quantity__input");
    qtyInputs.forEach(input => {
      input.setAttribute('min', min);
      input.setAttribute('step', increment);
      input.setAttribute('value', min);
      input.value = min;

      // Snap below-minimum values up on blur/change. Attached once and reads the
      // live min attribute: per-variant closures piled up, so a 100-minimum
      // variant kept forcing 100 after switching to a 1-minimum one.
      if (!input.dataset.minGuard) {
        input.dataset.minGuard = '1';
        const clampToMin = function() {
          if (SPID.state.dropshipRule) return;
          const currentMin = parseInt(this.getAttribute('min')) || 1;
          const currentValue = parseInt(this.value);
          if (isNaN(currentValue) || currentValue < currentMin) {
            this.value = currentMin;
          }
        };
        input.addEventListener('blur', clampToMin);
        input.addEventListener('change', clampToMin);
      }
    });

    const priceMinEl = document.querySelector('span.price-min');
    if (min > 1 && min != increment) {
      if (priceMinEl) {
        priceMinEl.classList.remove("hide");
        priceMinEl.innerHTML = '<span class="orange-bg price-min">Minimum Quantity ' + min + '</span>';
      }
    } else if(min == increment && min > 1) {
      if (priceMinEl) {
        priceMinEl.classList.remove("hide");
        priceMinEl.innerHTML = '<span class="green-font price-min">ea. <span class="orange-bg">Sold in Packs of ' + min + '</span></span>';
      }
    } else {
      if (priceMinEl) priceMinEl.classList.add("hide");
    }

    setTimeout(() => {
      // updateTablePrice now clears the table skeleton itself, on every exit
      // path including its early returns, so there is no explicit clear here.
      updateTablePrice();
    }, 1000);
  }

  function handleWindowLoad() {
    const body = document.body;
    if (body.classList.contains("product") || body.classList.contains("product.fpd") ||
        body.classList.contains("product.fpd-dynamic") || body.classList.contains("product.custom-badge-buddies") || body.classList.contains("product.custom-badge-bottoms")) {
      setTimeout(() => updateTablePrice(), 200);
    }

    // Check if product has no reviews and show no-review div
    const productStarsDiv = document.getElementById('product_just_stars');
    if (productStarsDiv) {
      const hasReviewContent = productStarsDiv.textContent.trim().length > 0 &&
                               productStarsDiv.querySelector('.star') ||
                               productStarsDiv.querySelector('.rating') ||
                               productStarsDiv.querySelector('[class*="star"]') ||
                               productStarsDiv.querySelector('[class*="review"]');

      if (!hasReviewContent) {
        const noReviewDiv = document.querySelector('.no-review');
        if (noReviewDiv) noReviewDiv.classList.remove('hidden');
      }
    }

    setTimeout(function() {
      // If we have product data, use it for SKU updates
      if (SPID.state.productData && SPID.state.productData.variants) {
        const sectionElements = document.querySelectorAll('[id^="shopify-section-"]');
        sectionElements.forEach((section) => {
          const variantSKU = section.querySelector('.variant-sku');
          const inputSelects = section.querySelectorAll('.single-option-selector');

          if (variantSKU && inputSelects.length > 0) {
            const inputValues = [];

            inputSelects.forEach((input) => {
              inputValues.push(input.value);
              input.addEventListener('change', (evt) => {
                const currentValue = evt.currentTarget.value.toString();
                const changedIndex = Array.from(inputSelects).indexOf(evt.target);
                inputValues[changedIndex] = currentValue;
                variantSKU.innerText = ' ';

                // Find matching variant
                SPID.state.productData.variants.forEach((variant) => {
                  if (JSON.stringify(variant.options) == JSON.stringify(inputValues)) {
                    variantSKU.innerText = variant.sku || '';
                  }
                });
              });
            });
          }
        });
      } else {
        // Fallback to original method if product data not available
        const productJson = document.querySelectorAll('[id^=ProductJson-]');
        if (productJson.length > 0) {
          productJson.forEach((product) => {
            const sectionId = product.id.replace("ProductJson-", "shopify-section-");
            const variantSKU = document.querySelector('#' + sectionId + ' .variant-sku');
            const inputSelects = document.querySelectorAll('#' + sectionId + ' .single-option-selector');
            const productInfo = JSON.parse(product.innerHTML);
            const inputValues = [];

            inputSelects.forEach((input) => {
              inputValues.push(input.value);
              input.addEventListener('change', (evt) => {
                const currentValue = evt.currentTarget.value.toString();
                const changedIndex = Array.from(inputSelects).indexOf(evt.target);
                inputValues[changedIndex] = currentValue;
                variantSKU.innerText = ' ';
                productInfo.variants.forEach((variant) => {
                  if (JSON.stringify(variant.options) == JSON.stringify(inputValues)) {
                    variantSKU.innerText = variant.sku;
                  }
                });
              });
            });
          });
        }
      }
    }, 100);
  }

  // Total price + volume table update later than Dawn's section swap (via
  // mapMsrpLogic/updateTablePrice, which is gated on Regios), so they get a
  // longer skeleton window than product-info's `.variant-loading` fetch state:
  // from the moment an option is picked (option-value-selection-change fires
  // before the fetch) until updateTablePrice has run. Clearing them on the
  // shorter fetch window would flash a stale total/table. Latest pick wins and a
  // safety timeout guarantees the state never sticks.
  let tableLoadingSeq = 0;

  function beginVariantTableLoading() {
    const pi = document.querySelector('product-info');
    if (!pi) return;
    tableLoadingSeq += 1;
    const mySeq = tableLoadingSeq;

    // Size the table skeleton's rows to the real table so the row count matches.
    // Measured before the table is hidden; the CSS mask uses --skel-row-pitch.
    const table = document.querySelector('.regios-dopp-generic-volume-pricing-table');
    if (table) {
      const rowCount = table.querySelectorAll('tr').length;
      const tableHeight = table.getBoundingClientRect().height;
      if (rowCount > 0 && tableHeight > 0) {
        pi.style.setProperty('--skel-row-pitch', (tableHeight / rowCount).toFixed(1) + 'px');
      }
    }

    pi.classList.add('variant-table-loading');
    setTimeout(function () {
      if (mySeq === tableLoadingSeq) pi.classList.remove('variant-table-loading');
    }, 4000);
  }

  function endVariantTableLoading() {
    const pi = document.querySelector('product-info');
    if (pi) pi.classList.remove('variant-table-loading');
  }

  // Published so custom-product-volume-pricing.js can clear the skeleton the
  // moment updateTablePrice has run, instead of it waiting out a fixed delay.
  SPID.endVariantTableLoading = endVariantTableLoading;

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.optionValueSelectionChange) {
    subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, beginVariantTableLoading);
  }

  SPID.optionLogic = optionLogic;
  SPID.handleWindowLoad = handleWindowLoad;

  // Window load events
  window.addEventListener('load', function() {
    // Ensure product data is loaded
    loadProductData().then(() => {
      handleWindowLoad();
    });
  });

  // Monitor URL changes for ?variant= param
  let currentParams = new URLSearchParams(window.location.search).toString();
  const urlObserver = new MutationObserver(() => {
    const newParams = new URLSearchParams(window.location.search).toString();
    if (currentParams !== newParams) {
      currentParams = newParams;
      const myParam = new URLSearchParams(window.location.search).get('variant');
      debugLog('Query parameter changed:', myParam);
      if (myParam) {
        // Ensure product data is loaded before processing variant
        loadProductData().then(() => {
          optionLogic(myParam);
        });
      }
    }
  });

  urlObserver.observe(document, { subtree: true, childList: true });

  // Announce this module so main-product.liquid can leave it out safely.
  if (window.SPID && SPID.register) SPID.register('variant-switch');
})();
